import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Drawer, Flex, Form, Input, InputNumber, Modal, Radio, Select, Space, Table, Tag, Typography } from "antd";
import { BarcodeOutlined } from "@ant-design/icons";
import { useDeferredValue, useState } from "react";
import { useNavigate } from "react-router";
import { api, unwrap } from "@/api/client";
import { useApplyFieldErrors, useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { CLIENT_PAGING } from "@/components/list/useListPage";
import { useUrlFilter } from "@/lib/useUrlFilter";
import { LocationKindTag, NoWms, useWmsWarehouse, type Location } from "./common";
import { ZoneInput, ZonesDrawer, useZones } from "./Zones";

type BulkValues = { zone: string; racks: [number, number]; levels: [number, number]; bins: [number, number] };
type OneValues = { zone?: string; rack?: string; level?: string; bin?: string; code?: string; kind: "storage" | "packing" | "damaged" | "quarantine" | "receiving" };

/** Gudang > Lokasi: the shelves of a WMS warehouse (zone › rack › level › bin). */
export function Locations() {
  const { t, formatNumber } = useI18n();
  const { can } = useAuth();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const { current, loading } = useWmsWarehouse();
  const [search, setSearch] = useUrlFilter<string>("q", "");
  const q = useDeferredValue(search.trim());
  const [adding, setAdding] = useState<"one" | "bulk" | null>(null);
  const [open, setOpen] = useState<Location | null>(null);
  const [zonesOpen, setZonesOpen] = useState(false);
  // "" every zone, "-" locations without one.
  const [zone, setZone] = useUrlFilter<string>("zone", "");
  const [selected, setSelected] = useState<string[]>([]);
  const [zoning, setZoning] = useState(false);
  const manage = can("settings.warehouses", "warehouse.manage");
  const zones = useZones(current?.id);
  const navigate = useNavigate();

  const list = useQuery({
    queryKey: ["locations", current?.id, q, zone],
    queryFn: () =>
      unwrap(
        api.GET("/v1/warehouses/{warehouseID}/locations", {
          params: { path: { warehouseID: current!.id }, query: { ...(q ? { q } : {}), ...(zone ? { zone: zone === "-" ? "" : zone } : {}) } },
        }),
      ),
    enabled: !!current,
  });
  const setStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "active" | "archived" }) =>
      unwrap(api.PATCH("/v1/locations/{locationID}", { params: { path: { locationID: id } }, body: { status } })),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["locations"] }),
    onError: (err) => void message.error(errorText(err)),
  });

  if (!loading && !current) return <NoWms />;
  return (
    <>
      <Flex justify="space-between" align="center" wrap gap={12} style={{ marginBottom: 16 }}>
        <Typography.Title level={2} style={{ margin: 0, fontSize: 28 }}>
          {t("wms.loc.title")}
        </Typography.Title>
        <Space wrap>
          <Button icon={<BarcodeOutlined />} onClick={() => navigate("/app/tools/barcodes?kind=location")}>
            {t("bc.title")}
          </Button>
          {manage && (
            <>
              <Button onClick={() => setZonesOpen(true)}>{t("wms.zone.title")}</Button>
              <Button onClick={() => setZoning(true)}>{selected.length ? t("wms.loc.zoneSelected", { n: selected.length }) : t("wms.loc.zoneAssign")}</Button>
              <Button onClick={() => setAdding("one")}>{t("wms.loc.add")}</Button>
              <Button type="primary" onClick={() => setAdding("bulk")}>
                {t("wms.loc.bulk")}
              </Button>
            </>
          )}
        </Space>
      </Flex>
      <Flex gap={12} wrap style={{ marginBottom: 16 }}>
        <Input.Search allowClear placeholder={t("wms.loc.search")} value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 260 }} />
        <Select<string>
          value={zone}
          onChange={setZone}
          style={{ width: 200 }}
          options={[
            { value: "", label: t("wms.loc.allZones") },
            ...(zones.data?.items ?? []).map((z) => ({ value: z.code, label: z.name ? `${t("wms.zone.tag", { code: z.code })} · ${z.name}` : t("wms.zone.tag", { code: z.code }) })),
            { value: "-", label: t("wms.zone.none") },
          ]}
        />
      </Flex>
      <Table<Location>
        rowKey="id"
        loading={list.isPending}
        dataSource={list.data?.items ?? []}
        pagination={CLIENT_PAGING}
        locale={{ emptyText: t("wms.loc.empty") }}
        rowSelection={
          manage
            ? {
                selectedRowKeys: selected,
                onChange: (keys) => setSelected(keys as string[]),
                getCheckboxProps: (l) => ({ disabled: l.system }),
              }
            : undefined
        }
        onRow={(l) => ({ onClick: () => setOpen(l), style: { cursor: "pointer" } })}
        columns={[
          {
            title: t("wms.loc.code"),
            dataIndex: "code",
            render: (v: string, l) => (
              <Space>
                <Typography.Text strong className="z-num">
                  {v}
                </Typography.Text>
                {l.system && <Tag>{t("wms.loc.system")}</Tag>}
                {l.status === "archived" && <Tag>{t("catalog.archived")}</Tag>}
                {l.countId && <Tag color="warning">{t("wms.count.frozen")}</Tag>}
              </Space>
            ),
          },
          { title: t("wms.loc.zone"), dataIndex: "zone", width: 90, render: (v: string) => (v ? <span className="z-num">{v}</span> : "—") },
          { title: t("wms.loc.kind"), dataIndex: "kind", width: 150, render: (k: string) => <LocationKindTag kind={k} /> },
          { title: t("wms.loc.skus"), dataIndex: "skus", width: 90, align: "right", render: (v: number) => <span className="z-num">{formatNumber(v)}</span> },
          { title: t("wms.loc.units"), dataIndex: "units", width: 110, align: "right", render: (v: number) => <span className="z-num">{formatNumber(v)}</span> },
          ...(manage
            ? [
                {
                  width: 120,
                  render: (_: unknown, l: Location) =>
                    !l.system && (
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setStatus.mutate({ id: l.id, status: l.status === "active" ? "archived" : "active" });
                        }}
                      >
                        {l.status === "active" ? t("wms.loc.archive") : t("wms.loc.restore")}
                      </Button>
                    ),
                },
              ]
            : []),
        ]}
      />
      {current && <BulkModal open={adding === "bulk"} warehouseId={current.id} onClose={() => setAdding(null)} />}
      {current && <OneModal open={adding === "one"} warehouseId={current.id} onClose={() => setAdding(null)} />}
      <Contents location={open} onClose={() => setOpen(null)} />
      {current && <ZonesDrawer open={zonesOpen} warehouseId={current.id} onClose={() => setZonesOpen(false)} />}
      {current && (
        <ZoneAssignModal
          open={zoning}
          warehouseId={current.id}
          selected={selected}
          onClose={(done) => {
            setZoning(false);
            if (done) setSelected([]);
          }}
        />
      )}
    </>
  );
}

function BulkModal({ open, warehouseId, onClose }: { open: boolean; warehouseId: string; onClose: () => void }) {
  const { t, formatNumber } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<BulkValues>();
  const v = Form.useWatch([], form) as BulkValues | undefined;
  const span = (r?: [number, number]) => (r && r[1] >= r[0] ? r[1] - r[0] + 1 : 0);
  const count = span(v?.racks) * span(v?.levels) * span(v?.bins);
  const save = useMutation({
    mutationFn: (x: BulkValues) =>
      unwrap(
        api.POST("/v1/warehouses/{warehouseID}/locations/bulk", {
          params: { path: { warehouseID: warehouseId } },
          body: {
            zone: x.zone,
            racks: { from: x.racks[0], to: x.racks[1] },
            levels: { from: x.levels[0], to: x.levels[1] },
            bins: { from: x.bins[0], to: x.bins[1] },
          },
        }),
      ),
    onSuccess: (r) => {
      void message.success(t("wms.loc.bulkDone", { created: r.created, skipped: r.skipped }));
      void queryClient.invalidateQueries({ queryKey: ["locations"] });
      onClose();
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  const range = (name: keyof BulkValues, label: string) => (
    <Form.Item label={label} required>
      <Space.Compact>
        <Form.Item name={[name, 0]} noStyle>
          <InputNumber min={1} max={99} placeholder={t("wms.loc.from")} style={{ width: 90 }} />
        </Form.Item>
        <Form.Item name={[name, 1]} noStyle>
          <InputNumber min={1} max={99} placeholder={t("wms.loc.to")} style={{ width: 90 }} />
        </Form.Item>
      </Space.Compact>
    </Form.Item>
  );
  return (
    <Modal
      open={open}
      title={t("wms.loc.bulkTitle")}
      okText={t("wms.loc.bulk")}
      cancelText={t("common.cancel")}
      okButtonProps={{ disabled: count === 0 || count > 5000 }}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      onCancel={onClose}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">{t("wms.loc.bulkBody")}</Typography.Paragraph>
      <Form form={form} layout="vertical" requiredMark={false} initialValues={{ zone: "A", racks: [1, 5], levels: [1, 4], bins: [1, 5] }} onFinish={(x) => save.mutate(x)}>
        <Form.Item name="zone" label={t("wms.loc.zone")} extra={t("wms.loc.zoneHelp")} rules={[{ required: true, pattern: /^[A-Za-z0-9]{1,6}$/, message: t("field.invalid") }]}>
          <ZoneInput warehouseId={warehouseId} />
        </Form.Item>
        <Flex gap={16} wrap>
          {range("racks", t("wms.loc.rack"))}
          {range("levels", t("wms.loc.level"))}
          {range("bins", t("wms.loc.bin"))}
        </Flex>
        <Typography.Text strong>{t("wms.loc.bulkCount", { n: formatNumber(count) })}</Typography.Text>
      </Form>
    </Modal>
  );
}

function OneModal({ open, warehouseId, onClose }: { open: boolean; warehouseId: string; onClose: () => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<OneValues>();
  const save = useMutation({
    mutationFn: (x: OneValues) => unwrap(api.POST("/v1/warehouses/{warehouseID}/locations", { params: { path: { warehouseID: warehouseId } }, body: x })),
    onSuccess: () => {
      void message.success(t("wms.loc.created"));
      void queryClient.invalidateQueries({ queryKey: ["locations"] });
      onClose();
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  return (
    <Modal open={open} title={t("wms.loc.add")} okText={t("common.save")} cancelText={t("common.cancel")} confirmLoading={save.isPending} onOk={() => form.submit()} onCancel={onClose} destroyOnHidden>
      <Form form={form} layout="vertical" requiredMark={false} initialValues={{ kind: "storage" }} onFinish={(x) => save.mutate(x)}>
        <Form.Item name="zone" label={t("wms.loc.zone")} extra={t("wms.loc.zoneHelp")} rules={[{ pattern: /^[A-Za-z0-9]{0,6}$/, message: t("field.invalid") }]}>
          <ZoneInput warehouseId={warehouseId} allowEmpty />
        </Form.Item>
        <Flex gap={12}>
          {(["rack", "level", "bin"] as const).map((f) => (
            <Form.Item key={f} name={f} label={t(`wms.loc.${f}`)} style={{ flex: 1 }}>
              <Input maxLength={6} className="z-num" />
            </Form.Item>
          ))}
        </Flex>
        <Form.Item name="code" label={t("wms.loc.code")} extra={t("wms.loc.codeHelp")}>
          <Input maxLength={32} className="z-num" />
        </Form.Item>
        <Form.Item name="kind" label={t("wms.loc.kind")}>
          <Select options={(["storage", "packing", "damaged", "quarantine", "receiving"] as const).map((k) => ({ value: k, label: t(`wms.loc.kind.${k}`) }))} />
        </Form.Item>
      </Form>
    </Modal>
  );
}

type ZoneAssignValues = { scope: "selected" | "prefix"; codePrefix: string; zone: string };

/** Puts locations in a zone: the ticked ones, or every code that starts with a prefix. */
function ZoneAssignModal({ open, warehouseId, selected, onClose }: { open: boolean; warehouseId: string; selected: string[]; onClose: (done: boolean) => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const applyFieldErrors = useApplyFieldErrors();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<ZoneAssignValues>();
  const scope = Form.useWatch("scope", form);
  const save = useMutation({
    mutationFn: (v: ZoneAssignValues) =>
      unwrap(
        api.POST("/v1/warehouses/{warehouseID}/locations/zone", {
          params: { path: { warehouseID: warehouseId } },
          body: v.scope === "selected" ? { locationIds: selected, zone: v.zone ?? "" } : { codePrefix: v.codePrefix, zone: v.zone ?? "" },
        }),
      ),
    onSuccess: (r) => {
      void message.success(t("wms.loc.zoneMoved", { n: r.moved }));
      for (const k of ["locations", "zones"]) void queryClient.invalidateQueries({ queryKey: [k] });
      onClose(true);
    },
    onError: (err) => {
      if (!applyFieldErrors(form, err)) void message.error(errorText(err));
    },
  });
  return (
    <Modal
      open={open}
      title={t("wms.loc.zoneAssign")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={save.isPending}
      onOk={() => form.submit()}
      onCancel={() => onClose(false)}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">{t("wms.loc.zoneAssignBody")}</Typography.Paragraph>
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ scope: selected.length ? "selected" : "prefix", codePrefix: "", zone: "" }}
        onFinish={(v) => save.mutate(v)}
      >
        <Form.Item name="scope">
          <Radio.Group
            style={{ display: "flex", flexDirection: "column", gap: 6 }}
            options={[
              { value: "selected", label: t("wms.loc.zoneScopeSelected", { n: selected.length }), disabled: selected.length === 0 },
              { value: "prefix", label: t("wms.loc.zoneScopePrefix") },
            ]}
          />
        </Form.Item>
        {scope === "prefix" && (
          <Form.Item name="codePrefix" label={t("wms.loc.codePrefix")} extra={t("wms.loc.codePrefixHelp")} rules={[{ required: true, message: t("field.required") }]}>
            <Input maxLength={32} className="z-num" style={{ width: 200 }} onChange={(e) => form.setFieldValue("codePrefix", e.target.value.toUpperCase())} />
          </Form.Item>
        )}
        <Form.Item name="zone" label={t("wms.loc.zoneTarget")} extra={t("wms.loc.zoneTargetHelp")} rules={[{ pattern: /^[A-Za-z0-9]{0,6}$/, message: t("field.invalid") }]}>
          <ZoneInput warehouseId={warehouseId} allowEmpty />
        </Form.Item>
      </Form>
    </Modal>
  );
}

function Contents({ location, onClose }: { location: Location | null; onClose: () => void }) {
  const { t, formatNumber } = useI18n();
  const items = useQuery({
    queryKey: ["locations", "contents", location?.id],
    queryFn: () => unwrap(api.GET("/v1/inventory/locations/{locationID}", { params: { path: { locationID: location!.id } } })),
    enabled: location !== null,
  });
  return (
    <Drawer open={location !== null} onClose={onClose} title={location ? t("wms.loc.contents", { code: location.code }) : ""} size="large" destroyOnHidden>
      <Table
        size="small"
        rowKey={(b) => `${b.skuId}/${b.lotId ?? ""}/${b.condition}`}
        loading={items.isPending}
        dataSource={items.data?.items ?? []}
        pagination={false}
        locale={{ emptyText: t("wms.loc.contentsEmpty") }}
        columns={[
          { title: t("catalog.code"), dataIndex: "skuCode", render: (v: string) => <span className="z-num">{v}</span> },
          { title: t("common.name"), dataIndex: "skuName" },
          { title: t("inventory.bin.lot"), dataIndex: "lotNo", render: (v: string | null, b) => (v ? `${v}${b.expiresOn ? ` · ${b.expiresOn}` : ""}` : "—") },
          { title: t("inventory.condition"), dataIndex: "condition", render: (v: string) => t(`inventory.condition.${v as "sellable"}`) },
          { title: t("wms.loc.units"), dataIndex: "quantity", align: "right", render: (v: number) => <span className="z-num">{formatNumber(v)}</span> },
          { title: t("inventory.bin.reserved"), dataIndex: "reserved", align: "right", render: (v: number) => <span className="z-num">{formatNumber(v)}</span> },
        ]}
      />
    </Drawer>
  );
}
